// Ownership Roles management page.
// Simple table with label, current owner, reassign (transfer) and delete actions.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { ownershipRolesApi, usersApi } from '@/lib/api/usersRoles'
import type { OwnershipRole } from '@/lib/api/usersRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/lib/hooks/use-toast'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { ArrowRightLeft, Trash2, Search, Loader2 } from 'lucide-react'

export function OwnershipRolesPage() {
  const { slug } = useTenant()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const ownApi = ownershipRolesApi(api)

  const [transferTarget, setTransferTarget] = useState<OwnershipRole | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<OwnershipRole | null>(null)
  const [deleteError, setDeleteError] = useState<{ blockers: { table: string; count: number }[] } | null>(null)

  const { data: roles, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.ownershipRoles.all(slug),
    queryFn: () => ownApi.list(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ownApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ownershipRoles.all(slug) })
      toast({ title: 'Ownership role deleted' })
      setDeleteTarget(null)
      setDeleteError(null)
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 409) {
        const body = err.body as Record<string, unknown> | null
        const blockers = Array.isArray(body?.blockers) ? body.blockers as { table: string; count: number }[] : []
        setDeleteError({ blockers })
        return
      }
      toast({
        title: 'Failed to delete',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-3 p-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load ownership roles"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="space-y-4 p-6">
      <div>
        <h2 className="text-lg font-semibold">Ownership Roles</h2>
        <p className="text-sm text-muted-foreground">
          Ownership roles assign accountability for registers, risks, controls, and actions.
          Each ownership role has exactly one current owner.
        </p>
      </div>

      {roles && roles.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No ownership roles found. They are created when registers, categories, and domain objects are set up.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">Label</th>
                <th className="px-4 py-3 text-left font-medium">Current Owner</th>
                <th className="px-4 py-3 text-left font-medium">Created</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles?.map((role) => (
                <tr key={role.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">{role.label}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {role.owner_display_name}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(role.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTransferTarget(role)}
                        title="Reassign ownership"
                      >
                        <ArrowRightLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDeleteError(null)
                          setDeleteTarget(role)
                        }}
                        title="Delete ownership role"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Transfer dialog */}
      {transferTarget && (
        <TransferDialog
          role={transferTarget}
          open={!!transferTarget}
          onOpenChange={(open) => !open && setTransferTarget(null)}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteError(null) } }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete ownership role</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteError ? (
                  <div className="space-y-2">
                    <span>
                      Cannot delete <strong>{deleteTarget.label}</strong> because it is
                      still referenced by:
                    </span>
                    <ul className="ml-4 list-disc text-sm">
                      {deleteError.blockers.map((b) => (
                        <li key={b.table}>
                          {b.count} {b.table.replace(/_/g, ' ')}
                        </li>
                      ))}
                    </ul>
                    <span className="text-xs">
                      Reassign or remove these references before deleting.
                    </span>
                  </div>
                ) : (
                  <>
                    Delete ownership role <strong>{deleteTarget.label}</strong> (currently
                    owned by {deleteTarget.owner_display_name})? This cannot be undone.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>
                {deleteError ? 'Close' : 'Cancel'}
              </AlertDialogCancel>
              {!deleteError && (
                <AlertDialogAction
                  onClick={() => deleteMutation.mutate(deleteTarget.id)}
                  disabled={deleteMutation.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}

// --- Transfer Dialog ---

function TransferDialog({
  role,
  open,
  onOpenChange,
}: {
  role: OwnershipRole
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { slug } = useTenant()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const ownApi = ownershipRolesApi(api)
  const usersClient = usersApi(api)

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)

  const { data: allUsers, isLoading: loadingUsers } = useQuery({
    queryKey: queryKeys.users.list(slug, { search: debouncedSearch || undefined }),
    queryFn: () => usersClient.list({ search: debouncedSearch || undefined }),
    enabled: open,
  })

  const transferMutation = useMutation({
    mutationFn: (newOwnerId: string) => ownApi.transfer(role.id, newOwnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ownershipRoles.all(slug) })
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all(slug) })
      toast({ title: 'Ownership transferred' })
      handleClose(false)
    },
    onError: (err) => {
      toast({
        title: 'Failed to transfer',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  // Filter out current owner
  const availableUsers = allUsers?.filter((u) => u.id !== role.current_owner_id) ?? []

  function handleClose(isOpen: boolean) {
    if (!isOpen) setSearchInput('')
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[80vh] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Reassign: {role.label}</DialogTitle>
          <DialogDescription>
            Currently owned by <strong>{role.owner_display_name}</strong>.
            Select a new owner.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>

        <div className="max-h-64 overflow-y-auto">
          {loadingUsers ? (
            <div className="space-y-2 py-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : availableUsers.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {debouncedSearch ? 'No matching users found.' : 'No other users available.'}
            </div>
          ) : (
            <div className="space-y-1 py-1">
              {availableUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {user.display_name || user.email}
                    </div>
                    {user.display_name && (
                      <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => transferMutation.mutate(user.id)}
                    disabled={transferMutation.isPending}
                  >
                    {transferMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Transfer'
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
